import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, Copy, RefreshCw, ShieldCheck } from "lucide-react";
import { Avatar, AvatarFallback } from "../../../ui/avatar";
import { Badge } from "../../../ui/badge";
import { Alert, AlertDescription } from "../../../ui/alert";
import { cn } from "../../../lib/utils";
import { dayTime, daysSince, humanise } from "../utils/format";
import { HoverTip } from "./DataBars";
import { useMoney } from "../utils/currency";

/**
 * Sticky identity header (§4.0) — who this player is, in one line the operator
 * keeps in view while scrolling the rest of the dashboard.
 *
 * Pseudonymous by design: username and player_id hash only. No email, phone or
 * real name may be added here.
 *
 * Every chip here is a themed `Badge` (variant="outline" is just the pill shell
 * — border, rounded-full, padding — with colour left to override), not a
 * reinvented span: Badge's own default/secondary/destructive/outline variants
 * don't cover a tier hue or an amber staleness state, so those tones are added
 * via className rather than by building a new chip primitive.
 */

/** Tier colour tokens, defined in index.css and exposed via tailwind.config.js. */
const TIER_CLASSES = {
  BRONZE: { text: "text-tier-bronze", dot: "bg-tier-bronze", border: "border-tier-bronze/45", ring: "ring-tier-bronze", tint: "bg-tier-bronze/15" },
  SILVER: { text: "text-tier-silver", dot: "bg-tier-silver", border: "border-tier-silver/45", ring: "ring-tier-silver", tint: "bg-tier-silver/15" },
  GOLD: { text: "text-tier-gold", dot: "bg-tier-gold", border: "border-tier-gold/45", ring: "ring-tier-gold", tint: "bg-tier-gold/15" },
  PLATINUM: { text: "text-tier-platinum", dot: "bg-tier-platinum", border: "border-tier-platinum/45", ring: "ring-tier-platinum", tint: "bg-tier-platinum/15" },
  DIAMOND: { text: "text-tier-diamond", dot: "bg-tier-diamond", border: "border-tier-diamond/45", ring: "ring-tier-diamond", tint: "bg-tier-diamond/15" },
};

const FALLBACK_TIER = {
  text: "text-muted-foreground",
  dot: "bg-muted-foreground",
  border: "border-border",
  ring: "ring-border",
  tint: "bg-muted",
};

export const TIER_ORDER = ["BRONZE", "SILVER", "GOLD", "PLATINUM", "DIAMOND"];

export const tierClasses = (tier) => TIER_CLASSES[String(tier || "").toUpperCase()] || FALLBACK_TIER;

/** Badge's own variants (default/secondary/destructive) don't carry per-tier
 * hue, so outline — just the pill shell — is the base every chip here builds on. */
const CHIP_BASE = "gap-1.5 font-normal";

export function TierBadge({ tier }) {
  const key = String(tier || "").toUpperCase();
  const style = tierClasses(key);
  return (
    <Badge variant="outline" className={cn(CHIP_BASE, "font-medium", style.border, style.text)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", style.dot)} aria-hidden="true" />
      {humanise(key)}
    </Badge>
  );
}

/** Neutral chip used for language and other quiet facts. */
export function QuietChip({ children, className }) {
  return (
    <Badge variant="outline" className={cn(CHIP_BASE, "bg-muted text-muted-foreground", className)}>
      {children}
    </Badge>
  );
}

/** Risk state chip — quiet when clear, red and unmissable when flagged (§4.7). */
export function RiskChip({ flagged }) {
  if (flagged) {
    return (
      <Badge variant="outline" className={cn(CHIP_BASE, "font-medium border-destructive/50 bg-destructive/10 text-destructive")}>
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
        Risk flagged
      </Badge>
    );
  }
  return (
    <QuietChip>
      <ShieldCheck className="h-3 w-3" aria-hidden="true" />
      No risk flags
    </QuietChip>
  );
}

/** Freshness chip — turns amber past seven days (§4.0, §7.5). */
export function FreshnessChip({ lastUpdatedAt, now }) {
  const age = daysSince(lastUpdatedAt, now);
  const stale = age !== null && age > 7;

  return (
    <Badge
      variant="outline"
      className={cn(
        CHIP_BASE,
        stale ? "border-chart-warning/45 bg-chart-warning/10 text-chart-warning" : "bg-muted text-muted-foreground"
      )}
    >
      <RefreshCw className="h-3 w-3" aria-hidden="true" />
      {stale ? `Last synced ${age} days ago` : `Data synced ${dayTime(lastUpdatedAt)}`}
    </Badge>
  );
}

/**
 * player_id is a hash operators paste into other tools, so it is click-to-copy.
 * Plain <button>, not the shared Button: Button's shortest size (h-9) is a
 * full form-control height, but this sits inline inside a text line at type
 * size — it needs no button chrome at all, just click semantics and a hover.
 */
function PlayerIdCopy({ playerId }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(playerId).then(done).catch(() => {});
    } else {
      done();
    }
  }, [playerId]);

  return (
    <HoverTip label={copied ? "Copied" : "Copy player ID"}>
      <button
        type="button"
        onClick={copy}
        // text-xs, not text-sm: monospace hash strings read wide at body size,
        // and the app's own id/hash columns (bettingHistoryTable.js) stay at
        // text-xs for exactly that reason.
        className="group inline-flex items-center gap-1.5 rounded px-1 py-0.5 font-mono text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        {playerId}
        {copied ? (
          <Check className="h-3 w-3 text-chart-positive" aria-hidden="true" />
        ) : (
          <Copy className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
        )}
      </button>
    </HoverTip>
  );
}

export function IdentityHeader({ profile, now, control }) {
  const tier = String(profile.vip_tier || "").toUpperCase();
  const style = tierClasses(tier);
  const initial = (profile.username || "?").charAt(0).toUpperCase();
  const rootRef = useRef(null);
  // Which currency the money on this page is in. Shown up here because a
  // crypto-brand player may have bet in three; every figure below is in this one.
  const { code: currencyCode } = useMoney();

  // Publish the header's rendered height so the anchor nav can stick directly
  // below it and section headings can clear the whole stack — same pattern
  // NavigationTabs uses for --sticky-tabs-offset.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    const publish = () => {
      document.documentElement.style.setProperty("--pi-header-height", `${el.offsetHeight}px`);
    };
    publish();

    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--pi-header-height");
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="sticky z-30 -mx-6 -mt-6 border-b bg-card px-6 py-3"
      style={{ top: "var(--sticky-tabs-offset, 99px)" }}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* Avatar with a tier-tinted ring */}
        <Avatar className={cn("ring-[1.5px]", style.ring)}>
          <AvatarFallback className={cn("text-sm font-semibold text-foreground", style.tint)}>
            {initial}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span className="text-lg font-semibold leading-none text-foreground">{profile.username}</span>
            <TierBadge tier={tier} />
            <QuietChip>{String(profile.language || "").toUpperCase()}</QuietChip>
            {currencyCode && (
              <HoverTip label="Reporting currency for every amount on this page">
                <span>
                  <QuietChip>{currencyCode}</QuietChip>
                </span>
              </HoverTip>
            )}
            <RiskChip flagged={profile.risk_flagged} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <PlayerIdCopy playerId={profile.player_id} />
            <span className="h-3 w-px bg-border" aria-hidden="true" />
            {/* session_count is a rolling counter: shown raw, never divided into (§2.4). */}
            <span className="tabular-nums">
              {profile.session_count} sessions, last seen {dayTime(profile.last_session_at)}
            </span>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {control}
          <FreshnessChip lastUpdatedAt={profile.last_updated_at} now={now} />
        </div>
      </div>
    </div>
  );
}

/**
 * Full-width banner under the header. Impossible to miss, repaints nothing else.
 *
 * Alert's destructive variant sets border + icon colour only, no background —
 * bg-destructive/10 is added here rather than reworking the shared variant,
 * which other callers (e.g. Login) rely on staying as-is. The absolute-positioned
 * icon layout Alert assumes (title + paragraph stacked to its right) is swapped
 * for a plain flex row, since this banner is a single inline sentence.
 */
export function RiskBanner() {
  return (
    <Alert
      variant="destructive"
      className="mt-4 flex items-center gap-3 bg-destructive/10 [&>svg]:static [&>svg]:mt-0 [&>svg~*]:pl-0"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
      <AlertDescription className="font-medium text-foreground">
        Risk flag active — review before rewarding or promoting this player.
      </AlertDescription>
    </Alert>
  );
}
